import { useEffect, useRef, useState } from 'react';
import { CAL_LINK, type Interest } from '../lib/qualification';

const CAL_ORIGIN = 'https://app.cal.com';
const SCRIPT_SRC = 'https://app.cal.com/embed/embed.js';

type CalConfigValue = string | string[] | boolean;
type CalApi = ((action: string, config?: unknown) => void) & {
  loaded?: boolean;
  ns?: Record<string, CalApi>;
  q: unknown[][];
};

declare global {
  interface Window {
    Cal?: CalApi;
  }
}

export type CalPrefill = {
  name: string;
  email: string;
  // Diagnostic-only fields. Omitted on the introductions intro call, which asks
  // none of these.
  interest?: Interest;
  projectBudgetUsd?: string;
  monthlyRevenueUsd?: string;
  notes: string;
  guests: string[];
};

function getCalApi(): CalApi {
  if (window.Cal) return window.Cal;

  const cal = ((...args: unknown[]) => {
    if (!cal.loaded) {
      // Cal's embed loader reads this object even when we use the default
      // (non-namespaced) API. The official bootstrap snippet creates it
      // before loading embed.js.
      cal.ns = cal.ns ?? {};
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      document.head.appendChild(script);
      cal.loaded = true;
    }
    cal.q.push(args);
  }) as CalApi;

  cal.q = [];
  cal.ns = {};
  window.Cal = cal;
  return cal;
}

export default function CalEmbed({
  prefill,
  calLink = CAL_LINK,
}: {
  prefill: CalPrefill;
  calLink?: string;
}) {
  const container = useRef<HTMLDivElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const node = container.current;
    if (!node) return;

    const config: Record<string, CalConfigValue> = {
      layout: 'month_view',
      theme: 'dark',
      name: prefill.name,
      email: prefill.email,
      notes: prefill.notes,
      guests: prefill.guests,
    };
    // Only attach the diagnostic booking-form fields when they are provided.
    if (prefill.interest !== undefined)
      config['What-are-you-interested-in'] = prefill.interest;
    if (prefill.projectBudgetUsd !== undefined)
      config['Project-budget--USD'] = prefill.projectBudgetUsd;
    if (prefill.monthlyRevenueUsd !== undefined)
      config['What-is-your-monthly-revenue'] = prefill.monthlyRevenueUsd;

    const cal = getCalApi();
    cal('init', { origin: CAL_ORIGIN });
    const fallbackTimer = window.setTimeout(() => {
      setLoaded(true);
      setFailed(true);
    }, 12_000);
    cal('on', {
      action: 'linkReady',
      callback: () => {
        window.clearTimeout(fallbackTimer);
        setLoaded(true);
      },
    });
    cal('on', {
      action: 'linkFailed',
      callback: () => {
        window.clearTimeout(fallbackTimer);
        setLoaded(true);
        setFailed(true);
      },
    });
    cal('inline', {
      elementOrSelector: node,
      calLink,
      config,
    });
    cal('ui', {
      theme: 'dark',
      hideEventTypeDetails: true,
      showTimezoneWhenEventDetailsHidden: true,
      styles: {
        branding: { brandColor: '#C9A96E' },
        body: { background: '#111111' },
      },
    });

    return () => {
      window.clearTimeout(fallbackTimer);
      node.replaceChildren();
    };
  }, [prefill, calLink]);

  return (
    <div className="grain relative min-h-[760px] overflow-hidden rounded-5xl border border-white/10 bg-charcoal">
      {!loaded && (
        <div
          aria-live="polite"
          className="absolute inset-0 z-10 flex items-center justify-center bg-charcoal"
        >
          <div className="flex flex-col items-center gap-4">
            <div className="h-10 w-10 animate-spin rounded-full border border-white/10 border-t-gold" />
            <p className="font-mono text-[0.65rem] uppercase tracking-label text-white/75">
              Loading Cal.com
            </p>
          </div>
        </div>
      )}

      {failed ? (
        <div className="flex min-h-[760px] flex-col items-center justify-center px-8 text-center">
          <p className="label mb-3">Calendar unavailable</p>
          <h2 className="font-display text-3xl font-light text-white">
            Open the booking calendar directly.
          </h2>
          <a
            href={`https://cal.com/${calLink}`}
            className="mt-8 inline-flex items-center justify-center rounded-full border border-gold bg-gold px-7 py-3.5 font-sans text-[0.7rem] uppercase tracking-label text-black transition-opacity hover:opacity-90"
          >
            Open Cal.com →
          </a>
        </div>
      ) : (
        <div ref={container} className="w-full" data-testid="cal-embed" />
      )}
    </div>
  );
}
